import { Customer, Prescription } from '../../../__generated__/graphql';
import React, { useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Image,
  Modal,
  Popover,
  Text,
  VStack,
} from 'native-base';
import currency from 'currency.js';
import { useTimezones } from '../../../Common/hooks/useTimezones';
import {
  iconForShippingMethod,
  nameForShippingMethod,
} from '../../../PatientOrder/utils/shippingMethods';
import { Icon } from '../../../Common/components/Icon';
import { faTruck } from '@fortawesome/pro-solid-svg-icons/faTruck';
import { Messages } from '../../../Messages/component/Messages';
import { faListAlt } from '@fortawesome/pro-solid-svg-icons/faListAlt';
import { faList } from '@fortawesome/pro-solid-svg-icons/faList';
import { usePatientPrescriptions } from '../../../DoctorPrescriptions/hooks/usePatientPrescriptions';
import { DoctorCustomerInfo } from '../../../Request/components/DoctorCustomerInfo';
import { UpdateShippingModal } from './UpdateShippingModal';
import { useOrder } from '../../hooks/useOrder';
import Loading from '../../../Common/components/Loading';
import { OrderItem } from './OrderItem';
import { Label } from './Label';

export const OrderModal = ({
  orderID,
  customerId,
  closeModal,
}: {
  orderID: string;
  customerId: string;
  closeModal: () => void;
}) => {
  const { order, bookOrder, cancelOrder, dispenseOrder, acceptOrder, orderEvents, loading } =
    useOrder(orderID, closeModal);

  const { prescriptions } = usePatientPrescriptions(order?.customer?.id ?? '');

  const { convertUTCDateToUserISO } = useTimezones();

  const [deliveryModalOpen, setDelivertModalOpen] = useState(false);

  const statusBadge = () => {
    if (order?.cancelled) return <Badge>Cancelled</Badge>;

    if (order?.booked) return <Badge bg={'green.300'}>Booked/Shipped</Badge>;

    if (order?.dispensed) return <Badge bg={'yellow.300'}>Labelled</Badge>;

    if (order?.charged) return <Badge bg={'yellow.300'}>Charged</Badge>;

    return <Badge bg={'orange.200'}>Pending</Badge>;
  };

  if (loading) {
    return (
      <Modal.Content mt={0} mb={0}>
        <Modal.CloseButton />
        <Modal.Body p={0} h={700} overflow={'hidden'} bg={'white'}>
          <Loading />
        </Modal.Body>
      </Modal.Content>
    );
  }

  return (
    <Modal.Content mt={0} mb={0}>
      <Modal.CloseButton />

      <Modal.Header>
        <HStack space={2} alignItems={'center'}>
          <Text fontWeight={600} fontSize={'md'}>{`Order for ${
            order?.customer?.name
          } - ${convertUTCDateToUserISO(order?.createdAt)?.format('DD/MM/YYYY hh:mm a')}`}</Text>

          {statusBadge()}
        </HStack>
      </Modal.Header>

      <Modal.Body p={0} h={700} overflow={'hidden'} bg={'white'}>
        <HStack space={0}>
          <VStack
            h={690}
            p={4}
            flex={1}
            space={1}
            style={{
              overflow: 'scroll',
            }}
          >
            <HStack
              mb={2}
              space={2}
              alignItems={'center'}
              borderBottomWidth={1}
              pb={1}
              borderBottomColor={'gray.300'}
            >
              <Icon icon={faListAlt} size={'md'} />
              <Text fontWeight={600} fontSize={'md'}>
                Customer
              </Text>
            </HStack>

            <DoctorCustomerInfo customer={order?.customer as Customer} minimal={true} />

            <HStack
              mt={2}
              space={2}
              mb={1}
              alignItems={'center'}
              borderBottomWidth={1}
              pb={1}
              borderBottomColor={'gray.300'}
            >
              <Icon icon={faList} size={'md'} />
              <Text fontWeight={600} fontSize={'md'}>
                Items
              </Text>
            </HStack>

            <VStack space={2} mb={2}>
              {order?.items?.map((item) => {
                const script = prescriptions?.find(
                  (a) => a.id === item.PrescriptionID,
                ) as Prescription;

                return <OrderItem item={item} key={item?.ID} order={order} script={script} />;
              })}

              <Box ml={16}>
                <Divider />
              </Box>

              <HStack ml={16} space={6}>
                <Text flex={1}>Subtotal</Text>

                <Text>{currency(order?.subtotal ?? 0).format()}</Text>
              </HStack>

              <HStack ml={16} space={6}>
                <Text flex={1}>Delivery</Text>

                <Text>{currency(order?.shipping ?? 0).format()}</Text>
              </HStack>

              {order?.consults && order?.consults > 0 ? (
                <HStack ml={16} space={6}>
                  <Text flex={1}>Consult</Text>

                  <Text>{currency(order?.consults ?? 0).format()}</Text>
                </HStack>
              ) : null}

              {order?.discounts && order?.discounts > 0 ? (
                <HStack ml={16} space={6}>
                  <Text flex={1}>Discounts</Text>

                  <Text>{currency(order?.discounts ?? 0).format()}</Text>
                </HStack>
              ) : null}

              <HStack ml={16} space={6}>
                <Text fontWeight={600} flex={1}>
                  Total
                </Text>

                <Text fontWeight={600}>{currency(order?.total ?? 0).format()}</Text>
              </HStack>
            </VStack>

            <HStack
              mt={4}
              space={2}
              alignItems={'center'}
              borderBottomWidth={1}
              pb={1}
              borderBottomColor={'gray.300'}
            >
              <Icon icon={faTruck} size={'md'} />
              <Text fontWeight={600} fontSize={'md'}>
                Delivery
              </Text>
            </HStack>

            <HStack alignItems={'center'} py={1}>
              <Text fontWeight={600} flex={1}>
                Method
              </Text>
              <HStack alignItems={'center'} h={8} flex={1} space={2}>
                <Image
                  borderRadius={'sm'}
                  source={iconForShippingMethod(order?.deliveryMethod)}
                  w={6}
                  h={6}
                />
                <Text>{nameForShippingMethod(order?.deliveryMethod)}</Text>
              </HStack>
            </HStack>

            <HStack py={1}>
              <Text fontWeight={600} flex={1}>
                Street
              </Text>

              <Text h={8} flex={1}>
                {order?.street}
              </Text>
            </HStack>

            <HStack py={1}>
              <Text fontWeight={600} flex={1}>
                Suburb
              </Text>

              <Text h={8} flex={1}>
                {order?.suburb}
              </Text>
            </HStack>

            <HStack py={1}>
              <Text fontWeight={600} flex={1}>
                Postcode & State
              </Text>
              <Text h={8} flex={1}>
                {order?.postcode} {order?.state}
              </Text>
            </HStack>

            <Popover
              placement={'top'}
              isOpen={deliveryModalOpen}
              onOpen={() => setDelivertModalOpen(true)}
              onClose={() => setDelivertModalOpen(false)}
              trigger={(triggerProps) => {
                return (
                  <Button
                    opacity={order?.cancelled || order?.booked ? 0.5 : 1}
                    disabled={order?.cancelled || order?.booked}
                    {...triggerProps}
                    variant={'outline'}
                    size={'sm'}
                  >
                    Change Delivery
                  </Button>
                );
              }}
            >
              <UpdateShippingModal order={order} close={() => setDelivertModalOpen(false)} />
            </Popover>

            <HStack
              mt={4}
              space={2}
              alignItems={'center'}
              borderBottomWidth={1}
              pb={1}
              borderBottomColor={'gray.300'}
            >
              <Icon icon={faList} size={'md'} />
              <Text fontWeight={600} fontSize={'md'}>
                Events
              </Text>
            </HStack>

            {orderEvents?.map((a) => (
              <Text key={a.event}>
                {convertUTCDateToUserISO(a?.timestamp)?.format('DD/MM/YYYY')} {a.event}
              </Text>
            ))}
          </VStack>

          <Box
            h={740}
            p={4}
            flex={1}
            space={1}
            style={{
              overflow: 'scroll',
            }}
            borderLeftColor={'gray.300'}
            borderLeftWidth={1}
            borderRightColor={'gray.300'}
            borderRightWidth={1}
          >
            {!order?.charged && (
              <VStack space={2}>
                {!order?.isPayInPerson && <Text fontWeight={600} fontSize={'lg'}>
                  Next Step: Accept Order
                </Text>}

                {!order?.isPayInPerson && <Text>
                  This charges the customers provided payment method. Any changes that changes the
                  order total must be made now else a refund will need to be processed.
                </Text>}

                {!order?.isPayInPerson && <Text>
                  <strong>Important: Afterpay orders must be verified as approved in the Afterpay portal before accepting the order.</strong>
                </Text>}

                {order?.isPayInPerson && <Text fontWeight={600} fontSize={'lg'}>
                  Next Step: Confirm payment received in person
                </Text>}

                {order?.isPayInPerson && <Text>
                  Confirm that payment has been processed in person with the customer to complete the order.
                </Text>}

                <Button onPress={acceptOrder} size={'sm'} bg={'green.600'}>
                  Accept Order
                </Button>

                <Button onPress={cancelOrder} size={'sm'} bg={'red.600'}>
                  Cancel Order
                </Button>
              </VStack>
            )}

            {order?.charged
              && !order?.dispensed
              && !order?.booked
              && !order?.deliveryMethod?.startsWith('pickup')
              && !order?.deliveryMethod?.startsWith('sameday')
              && (
              <VStack space={2}>
                <Text fontWeight={600} fontSize={'lg'}>
                  Next Step: Dispense Order
                </Text>

                <Text>This will mark the order as dispensed & generate a shipping label.</Text>

                <Button onPress={dispenseOrder} size={'sm'} bg={'green.600'}>
                  Dispense & Label Order
                </Button>
              </VStack>
            )}

            {order?.charged
              && !order?.dispensed
              && !order?.booked
              && order?.deliveryMethod?.startsWith('pickup')
              && !order?.deliveryMethod?.startsWith('sameday')
              && (
              <VStack space={2}>
                <Text fontWeight={600} fontSize={'lg'}>
                  Next Step: Dispense Order
                </Text>

                <Text>Confirm the customer has picked up their order and mark as dispensed.</Text>

                <Button onPress={dispenseOrder} size={'sm'} bg={'green.600'}>
                  Mark order as Picked Up
                </Button>
              </VStack>
            )}

            {order?.charged
              && !order?.dispensed
              && !order?.booked
              && !order?.deliveryMethod?.startsWith('pickup')
              && order?.deliveryMethod?.startsWith('sameday')
              && (
                <VStack space={2}>
                  <Text fontWeight={600} fontSize={'lg'}>
                    Next Step: Dispense Order
                  </Text>

                  <Text>Mark this order as dispensed via same day courier.</Text>

                  <Button onPress={dispenseOrder} size={'sm'} bg={'green.600'}>
                    Dispense order via Same Day Courier
                  </Button>
                </VStack>
              )}

            {order?.charged && order.dispensed && !order.booked && (
              <Label book={bookOrder} order={order} />
            )}

            {order?.charged && order.dispensed && order.booked && (
              <VStack space={2}>
                <Text fontWeight={600} fontSize={'lg'}>
                  Order Booked
                </Text>

                <Button onPress={closeModal} size={'sm'} bg={'green.600'}>
                  Close
                </Button>
              </VStack>
            )}
          </Box>

          <Box flex={1} bg={'white'} p={2} h={690}>
            {customerId && <Messages ThreadID={customerId} />}
          </Box>
        </HStack>
      </Modal.Body>
    </Modal.Content>
  );
};
